import DigiobcanApi, {
  type AdminReportDetail,
} from '../../../../api/DigiobcanApi';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import { shortId } from '../../../../lib/ids';
import { downloadMediaArchive } from '../../../../lib/mediaArchive';
import { useM } from '../../../../lib/query';
import { useLogger } from '../../../../providers/LoggerProvider';
import {
  __,
  isPreviewableMedia,
  toastError,
  toastSuccess,
} from '../../../../utils';
import CensorEditor from './CensorEditor';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import {
  Box,
  Button,
  Image,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Media = AdminReportDetail['media'][number];

type Props = {
  report: AdminReportDetail;
  onChange: () => Promise<unknown>;
  onPreview: (media: Media) => void;
  compact?: boolean;
  /**
   * Úradník podnet len vidí - nemá kategóriu, v ktorej by ho riešil
   * (`pruser:report-categories:read-all`). Prílohy sa dajú otvoriť a stiahnuť,
   * ale pridať ani zmazať nie: `adminAddReportMedia` aj `adminDeleteReportMedia`
   * to odmietnu 403. Rovnaký zámok ako archivácia, iný dôvod.
   */
  readOnly?: boolean;
};

/** „6 súborov · 10,3 MB" - slovenské skloňovanie, desatinná čiarka. */
const filesLabel = (count: number) => {
  if (count === 1) return __('1 súbor');
  if (count >= 2 && count <= 4) return `${count} ${__('súbory')}`;
  return `${count} ${__('súborov')}`;
};

const formatType = (mediaType?: string) => {
  if (!mediaType) return '';
  const [, subtype = ''] = mediaType.split('/');
  return subtype.toUpperCase().slice(0, 4);
};

const isImage = (mediaType?: string) => !!mediaType?.startsWith('image/');

/**
 * Fotky, ktoré vie API cenzurovať (`CENSORABLE_MEDIA_TYPES` v digiobcan-api).
 * GIF nie: z animácie by sa zakryl len prvý snímok.
 */
const CENSORABLE_MEDIA_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/tiff',
];

/**
 * Akcie pod dlaždicou - drobné, aby sa dve zmestili aj pod úzku dlaždicu.
 * `outline`, nie `text`: textové tlačidlo dostane rámik až pri prejdení
 * myšou a celý riadok sa pri tom pohne.
 */
const tileActionSx = {
  minWidth: 0,
  px: 1,
  py: 0.25,
  fontSize: 12,
  fontWeight: 600,
} as const;

const EnvelopeAttachments: React.FC<Props> = ({
  report,
  onChange,
  onPreview,
  compact = false,
  readOnly = false,
}) => {
  /**
   * Archivovaný podnet sa neupravuje - API každý zápis nad ním odmieta
   * (`409 REPORT_ARCHIVED`), takže aktívne tlačidlo by len sľubovalo chybu.
   * Prílohy zostávajú **čitateľné**: archív má ukázať, čo v podnete bolo.
   */
  const archived = !!report.archived_at;

  /** Prílohy sa nemenia. Dva rôzne dôvody, jeden zámok. */
  const locked = archived || readOnly;

  const { logger } = useLogger();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [pendingDelete, setPendingDelete] = React.useState<Media>();
  const [censoring, setCensoring] = React.useState<Media>();

  const media = (report.media ?? []) as Media[];

  const uploadMutation = useM(
    async (files: File[]) => {
      await DigiobcanApi.adminAddReportMedia(report.id, undefined, {
        media: files,
      } as never);
      return onChange();
    },
    {
      onSuccess: () => toastSuccess(__('Prílohy boli pridané.')),
      onError: (error) => {
        logger.error('Failed to add report media', {
          action: 'addReportMedia',
          error,
          data: { reportId: report.id },
        });
        toastError(__('Prílohu sa nepodarilo pridať.'));
      },
    },
  );

  const deleteMutation = useM(
    async (mediaId: string) => {
      await DigiobcanApi.adminDeleteReportMedia(report.id, mediaId);
      return onChange();
    },
    {
      onSuccess: () => {
        setPendingDelete(undefined);
        toastSuccess(__('Príloha bola odstránená.'));
      },
      onError: (error, mediaId) => {
        logger.error('Failed to delete report media', {
          action: 'deleteReportMedia',
          error,
          data: { reportId: report.id, mediaId },
        });
        setPendingDelete(undefined);
        toastError(__('Prílohu sa nepodarilo odstrániť.'));
      },
    },
  );

  const downloadMutation = useM(
    () =>
      downloadMediaArchive(media, `podnet-${shortId(report.id)}-prilohy.zip`),
    {
      onError: (error) => {
        logger.error('Failed to download report media', {
          action: 'downloadReportMedia',
          error,
          data: { reportId: report.id },
        });
        toastError(__('Prílohy sa nepodarilo stiahnuť.'));
      },
    },
  );

  const downloadAll = () => {
    if (!navigator.onLine) {
      toastError(__('Nie ste pripojený k internetu.'));
      return;
    }
    downloadMutation.mutate();
  };

  return (
    <Stack gap={1} sx={{ minWidth: 0 }}>
      <Stack
        direction='row'
        alignItems='center'
        gap={1}
        flexWrap='wrap'
        sx={{ rowGap: 0.5 }}
      >
        <Typography
          variant='overline'
          sx={{ fontWeight: 700, letterSpacing: '.05em' }}
        >
          {__('Prílohy')}
        </Typography>
        <Typography variant='caption' color='text.secondary' sx={{ flex: 1 }}>
          {media.length ? filesLabel(media.length) : ''}
        </Typography>

        {!compact && (
          <Stack direction='row' gap={1}>
            {/* `outline`, nie `text`: textové tlačidlo dostane rámik až pri
                prejdení myšou, pribudne mu pár pixelov a riadok s nadpisom
                sekcie pri tom poskočí. */}
            <Button
              variant='outline'
              size='small'
              disabled={!media.length || downloadMutation.isPending}
              onClick={downloadAll}
              startIcon={<FileDownloadOutlinedIcon />}
            >
              {__('Stiahnuť všetko')}
            </Button>
            <Button
              variant='outline'
              size='small'
              disabled={locked || uploadMutation.isPending}
              onClick={() => inputRef.current?.click()}
              startIcon={<FileUploadOutlinedIcon />}
            >
              {__('Pridať prílohu')}
            </Button>
          </Stack>
        )}
      </Stack>

      <input
        ref={inputRef}
        type='file'
        multiple
        hidden
        // Bez `accept`: API neodmieta žiadny typ prílohy, takže by ho nemal
        // odmietať ani dialóg na výber súboru.
        onChange={(event) => {
          const { files } = event.target;
          if (!files?.length) return;
          if (!navigator.onLine) {
            toastError(__('Nie ste pripojený k internetu.'));
            return;
          }
          // Kópia, nie `files` samotné: je to živý zoznam inputu, ktorý
          // vynulovanie nižšie vyprázdni skôr, než ho mutácia prečíta - API
          // potom dostalo 0 súborov a hlásilo úspech.
          uploadMutation.mutate(Array.from(files));
          // Bez tohto by sa ten istý súbor nedal nahrať dvakrát po sebe -
          // `change` sa pri rovnakej hodnote už nespustí.
          event.target.value = '';
        }}
      />

      {media.length ? (
        <Box
          sx={
            compact
              ? {
                  display: 'flex',
                  gap: 1,
                  overflowX: 'auto',
                  pb: 1,
                  // Na mobile pás scrolluje od okraja obrazovky, nie od
                  // paddingu sekcie.
                  mx: -1.75,
                  px: 1.75,
                }
              : {
                  // Na desktope mriežka cez celú šírku karty - pri jej
                  // bežnej šírke tri dlaždice do riadku.
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  gap: 1.5,
                }
          }
        >
          {media.map((item) => (
            // Fotka, názov a akcie sú jedna karta - v mriežke inak nebolo
            // jasné, ku ktorej fotke tlačidlá pod ňou patria.
            <Stack
              key={item.media_id ?? item.public_url}
              sx={{
                minWidth: 0,
                borderRadius: 1,
                overflow: 'hidden',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: 'background.paper',
                ...(compact && { width: 160, flex: 'none' }),
              }}
            >
              <Box
                onClick={() =>
                  isPreviewableMedia(item.media_type)
                    ? onPreview(item)
                    : window.open(item.public_url, '_blank', 'noopener')
                }
                sx={{
                  position: 'relative',
                  aspectRatio: '4 / 3',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  backgroundColor: 'neutralSurface',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {isImage(item.media_type) ? (
                  <>
                    {/* Fotka celá (`contain`), aby úradník videl, čo na nej je;
                        prázdne pruhy vedľa nej vypĺňa tá istá fotka rozmazaná.
                        Pozadie je CSS, nie druhý `<Image>` - pri mŕtvej adrese
                        by sa náhradný obrázok ukázal dvakrát. */}
                    <Box
                      aria-hidden
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url(${JSON.stringify(item.public_url)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'blur(12px) brightness(.85)',
                        // Rozmazanie pri okraji presvitá do priehľadna, zväčšenie
                        // ten svetlý lem schová za `overflow: hidden`.
                        transform: 'scale(1.2)',
                      }}
                    />
                    <Image
                      src={item.public_url}
                      alt={item.filename}
                      // Absolútne, nie `height: 100%`: dlaždica je grid
                      // s automatickým riadkom, v ňom sa percento výšky
                      // neodvodí a fotka na výšku by pretiekla a orezala sa.
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </>
                ) : (
                  <Typography
                    variant='caption'
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontWeight: 700,
                      backgroundColor: 'background.paper',
                      color: 'text.secondary',
                    }}
                  >
                    {formatType(item.media_type)}
                  </Typography>
                )}

                {item.censor && (
                  <Typography
                    variant='caption'
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      px: 0.75,
                      borderRadius: 0.5,
                      fontWeight: 700,
                      backgroundColor: 'rgba(38, 36, 32, .72)',
                      color: 'common.white',
                    }}
                  >
                    {__('Cenzurované')}
                  </Typography>
                )}
              </Box>

              <Typography
                variant='caption'
                noWrap
                title={item.filename}
                sx={{ px: 1, pt: 0.75, pb: locked ? 0.75 : 0 }}
              >
                {item.filename}
              </Typography>

              {!locked && (
                <Stack
                  direction='row'
                  justifyContent='center'
                  gap={0.75}
                  sx={{ px: 1, pt: 0.5, pb: 1 }}
                >
                  <Button
                    variant='outline'
                    size='small'
                    onClick={() => setPendingDelete(item)}
                    sx={tileActionSx}
                  >
                    {__('Odstrániť')}
                  </Button>
                  {CENSORABLE_MEDIA_TYPES.includes(item.media_type) && (
                    <Button
                      variant='outline'
                      size='small'
                      onClick={() => setCensoring(item)}
                      sx={tileActionSx}
                    >
                      {__('Cenzurovať')}
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            height: 110,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 1,
            border: (theme) => `1px dashed ${theme.palette.grey[400]}`,
          }}
        >
          <Typography variant='caption' color='text.secondary'>
            {__('Podnet nemá žiadne prílohy')}
          </Typography>
        </Box>
      )}

      {compact && (
        <Stack direction='row' gap={1}>
          <Button
            variant='outline'
            fullWidth
            disabled={locked || uploadMutation.isPending}
            onClick={() => inputRef.current?.click()}
            startIcon={<FileUploadOutlinedIcon />}
          >
            {__('Pridať prílohu')}
          </Button>
          <Button
            variant='outline'
            fullWidth
            disabled={!media.length || downloadMutation.isPending}
            onClick={downloadAll}
            startIcon={<FileDownloadOutlinedIcon />}
          >
            {__('Stiahnuť všetko')}
          </Button>
        </Stack>
      )}

      {censoring && (
        <CensorEditor
          reportId={report.id}
          media={censoring}
          onClose={() => setCensoring(undefined)}
          onSaved={onChange}
        />
      )}

      <ConfirmationDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(undefined)}
        title={__('Odstrániť prílohu')}
        // Príloha je dôkaz autora podnetu, nie poznámka úradu - mazanie sa preto
        // potvrdzuje, hoci prepínač nevhodnosti vedľa potvrdenie nemá.
        description={__(
          `Príloha "${pendingDelete?.filename ?? ''}" sa natrvalo odstráni z podnetu. Túto zmenu nie je možné vrátiť.`,
        )}
        confirmLabel={__('Odstrániť')}
        isLoading={deleteMutation.isPending}
        confirmDisabled={deleteMutation.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          if (!navigator.onLine) {
            toastError(__('Nie ste pripojený k internetu.'));
            return;
          }
          deleteMutation.mutate(pendingDelete.media_id);
        }}
      />
    </Stack>
  );
};

export default EnvelopeAttachments;
